import React, { useMemo, useEffect, useReducer, useCallback } from 'react';
import {
  User,
  getAuth,
  signOut,
  signInWithPopup,
  onAuthStateChanged,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from 'firebase/auth';

import axios from 'axios';
import { AppConfig } from 'src/utils/AppConfig';
import { firebaseApp } from './lib';
import { AuthContext } from './auth-context';
import { AuthUserType, ActionMapType, AuthStateType, UserInfo } from '../../types';
import { Endpoints, OcdFeatApi } from '../../../utils/Api';

const AUTH = getAuth(firebaseApp);

enum Types {
  USER = 'USER',
  NEW_USER_FROM_LOGIN = 'NEW_USER_FROM_LOGIN'
}

type Payload = {
  [Types.USER]: {
    user: AuthUserType;
  };
  [Types.NEW_USER_FROM_LOGIN]: {};
};

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
  isNewUserFromLogin: false
};

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.USER) {
    return {
      loading: false,
      user: action.payload.user,
      isNewUserFromLogin: action.payload.user ? state.isNewUserFromLogin : false
    };
  }
  if (action.type === Types.NEW_USER_FROM_LOGIN) {
    return {
      ...state,
      isNewUserFromLogin: true
    };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const getCurrentUser = async (user: User): Promise<UserInfo | null> => {
    try {
      const token = await user.getIdToken();
      const api = await OcdFeatApi(token);
      const { data } = await api.get(Endpoints.getUser);

      return data.data;
    } catch (err) {
      if (axios.isAxiosError(err) && err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  };

  const createUser = async ({user,first, last, dob, email}: {user: User, first: string, last: string, dob: string, email: string}): Promise<UserInfo> => {
    const token = await user.getIdToken();
    const api = await OcdFeatApi(token);
    const { data } = await api.post(Endpoints.createUser, {
      first,
      last,
      dob,
      email
    });

    return data.data;
  };


  const initialize = useCallback(() => {
    try {
      onAuthStateChanged(AUTH, async (user) => {
        if (user) {
          // get user
          const userInfo = await getCurrentUser(user)

          dispatch({
            type: Types.USER,
            payload: {
              user: {
                firebaseUser: user,
                ...(userInfo
                  ? { signedUp: true, ...userInfo }
                  : { signedUp: false }),
                externalProviderId: user.uid
              }
            }
          });
        } else {
          dispatch({
            type: Types.USER,
            payload: {
              user: null
            }
          });
        }
      });
    } catch (error) {
      dispatch({
        type: Types.USER,
        payload: {
          user: null
        }
      });
      throw error;
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const getAccessToken = useCallback(async () => {
    const { user } = state;
    return user?.firebaseUser?.getIdToken()!;
  }, [state])

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(AUTH, email, password);
  }, []);

  const loginWithGoogle = useCallback(async (from: 'login' | 'register') => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(AUTH, provider);

    if (from === 'login') {
      const additionalUserInfo = getAdditionalUserInfo(result);
      const isNewUserFromLogin = additionalUserInfo?.isNewUser;

      if (isNewUserFromLogin) {
        dispatch({
          type: Types.NEW_USER_FROM_LOGIN,
          payload: {}
        });
      }
    }
  }, []);
  
  const signUp = useCallback(
    async (user: User, first: string, last: string, dob: string) => {
      if (!state.user?.signedUp) {
        // sign up with api

        // get current user
        let userInfo = await getCurrentUser(user);
        if (userInfo) {
          // already have one
          throw new Error('user already exists');
        }

        // create user
        userInfo = await createUser({
          user,
          first,
          last,
          dob,
          email: user.email!,
        });

        dispatch({
          type: Types.USER,
          payload: {
            user: {
              firebaseUser: user,
              ...(userInfo
                ? { signedUp: true, ...userInfo }
                : { signedUp: false }),
              externalProviderId: user.uid
            }
          }
        });
      }
    },
    [state.user]
  );

  // REGISTER
  const register = useCallback(async (email: string, password: string) => {
    const { user } = await createUserWithEmailAndPassword(
      AUTH,
      email,
      password
    );
    await sendEmailVerification(user, {
      url: AppConfig.appUrl,
    });
  }, []);

  const resendEmailVerification = useCallback(async () => {
    await sendEmailVerification(state.user?.firebaseUser!, {
      url: AppConfig.appUrl,
    });
  }, [state.user?.firebaseUser]);

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(AUTH, email, {
      url: AppConfig.appUrl,
    });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      isNewUser: state.isNewUserFromLogin,
      method: 'firebase',
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      isUnauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
      register,
      signUp,
      forgotPassword,
      loginWithGoogle,
      resendEmailVerification,
      getAccessToken
    }),
    [
      status,
      state.user,
      state.isNewUserFromLogin,
      //
      login,
      logout,
      register,
      signUp,
      forgotPassword,
      loginWithGoogle,
      resendEmailVerification,
      getAccessToken
    ]
  );

  return (
    <AuthContext.Provider value={memoizedValue}>
      {children}
    </AuthContext.Provider>
  );
}
